import { useTranslations } from '@vocab/react';
import { Stack, Text } from 'braid-design-system';
import React, {
  forwardRef,
  useRef,
  type Ref,
  type RefObject,
  useContext,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { SECTION_CANDIDATE_SEARCH_RESULTS } from 'src/app/shared/constants/pageSectionName';
import {
  FEATURE_KEY_SELLING_POINTS,
  useFeatureToggleData,
} from 'src/app/shared/featureToggle';
import { JobSummary } from 'src/app/v1/views/components/JobSummary';
import { KeySellingPoints } from 'src/app/v1/views/components/KeySellingPoints';
import { EditFlowDataContext } from 'src/app/v1/views/context/cajaFlowData';
import { KeySellingPointsReadOnlyView } from 'src/app/v1/views/pages/Edit/components/KeySellingPointsReadOnlyView';
import { EditablePageSection } from 'src/app/v1/views/sections/EditablePageSection';
import { ContentLoader } from 'src/app/v1/views/shared/ContentLoader';
import type { AppState } from 'src/types/appState';
import type { JobSummaryRef, KeySellingPointsRef } from 'src/types/formFields';
import type { SectionProps, SectionRef } from 'src/types/sections';

import translations from './.vocab';

type ReadProps = {
  isKeySellingPointsEnabled?: boolean;
};

const Read = ({ isKeySellingPointsEnabled }: ReadProps) => {
  const { jobSummary } = useSelector(
    (state: AppState) => ({
      jobSummary: state.formData.jobSummary,
    }),
    shallowEqual,
  );

  const { t } = useTranslations(translations);

  const { loading } = useContext(EditFlowDataContext);

  return (
    <Stack space="large">
      <Stack space="medium">
        <Text weight="strong">
          {t('candidate-search-results-section-read-job-summary-label')}
        </Text>
        <ContentLoader loading={loading} lines={['85%', '85%', '50%']}>
          <Text>{jobSummary}</Text>
        </ContentLoader>
      </Stack>
      {isKeySellingPointsEnabled && <KeySellingPointsReadOnlyView />}
    </Stack>
  );
};

interface EditProps {
  refs: {
    jobSummaryRef: RefObject<JobSummaryRef>;
    keySellingPointsRef: RefObject<KeySellingPointsRef>;
  };
  isKeySellingPointsEnabled: boolean;
}

const Edit = ({ refs, isKeySellingPointsEnabled }: EditProps) => (
  <Stack space="medium">
    <JobSummary ref={refs.jobSummaryRef} />
    {isKeySellingPointsEnabled && (
      <KeySellingPoints ref={refs.keySellingPointsRef} />
    )}
  </Stack>
);

export const CandidateSearchResultsSection = forwardRef(
  (_props: SectionProps, ref: Ref<SectionRef>) => {
    const jobSummaryRef = useRef<JobSummaryRef>(null);
    const keySellingPointsRef = useRef<KeySellingPointsRef>(null);

    const isKeySellingPointsEnabled = useFeatureToggleData(
      FEATURE_KEY_SELLING_POINTS,
    );

    const { t } = useTranslations(translations);

    return (
      <EditablePageSection
        title={t('candidate-search-results-section-title')}
        name={SECTION_CANDIDATE_SEARCH_RESULTS}
        ref={ref}
        fields={[jobSummaryRef, keySellingPointsRef]}
        read={<Read isKeySellingPointsEnabled={isKeySellingPointsEnabled} />}
        edit={
          <Edit
            refs={{ jobSummaryRef, keySellingPointsRef }}
            isKeySellingPointsEnabled={isKeySellingPointsEnabled ?? false}
          />
        }
      />
    );
  },
);
